import React, { useState, useEffect } from 'react';
import { invitationAPI } from '../../lib/api';
import { useInvitationStore } from '../../store/invitationStore';
import { Users, Server, Check, AlertTriangle, Shield } from 'lucide-react';
import { useServerStore } from '../../store/serverStore';

interface InviteCardProps {
  inviteCode: string;
  compact?: boolean;
  inMessage?: boolean;
}

interface ServerInfo {
  id: string;
  name: string;
  icon_url: string | null;
  description: string | null;
  member_count: number;
}

interface InvitationInfo {
  code: string;
  server_id: string;
  max_uses: number;
  uses: number;
  expires_at: string | null;
  server_info?: ServerInfo;
  server?: ServerInfo;
}

const InviteCard: React.FC<InviteCardProps> = ({ inviteCode, inMessage = false }) => {
  // Each card manages its own local state — no shared global conflicts
  const [invitation, setInvitation] = useState<InvitationInfo | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [minimumLoadingDone, setMinimumLoadingDone] = useState(false);

  const { acceptInvitation } = useInvitationStore();
  const { servers } = useServerStore();

  useEffect(() => {
    let cancelled = false;
    setInvitation(null);
    setLoadError(false);
    setMinimumLoadingDone(false);
    const loadingTimer = window.setTimeout(() => {
      if (!cancelled) setMinimumLoadingDone(true);
    }, 300);

    invitationAPI.getInvitationInfo(inviteCode)
      .then(data => { if (!cancelled) setInvitation(data); })
      .catch(() => { if (!cancelled) setLoadError(true); });

    return () => {
      cancelled = true;
      window.clearTimeout(loadingTimer);
    };
  }, [inviteCode]);

  const serverInfo = invitation?.server_info || invitation?.server;
  const isAlreadyMember = serverInfo
    ? servers.some(s => s.id === serverInfo.id)
    : false;

  const handleJoin = async () => {
    if (joined || joining || isAlreadyMember) return;

    setJoining(true);
    setJoinError(null);
    try {
      const ok = await acceptInvitation(inviteCode);
      if (ok) setJoined(true);
      else setJoinError('Impossible de rejoindre le serveur.');
    } catch (e: any) {
      setJoinError(e?.message || 'Erreur');
    } finally {
      setJoining(false);
    }
  };

  // ── Loading skeleton ──────────────────────────────────────────────────────
  if (!minimumLoadingDone || (!invitation && !loadError)) {
    return (
      <div className={`invite-card-skeleton bg-bg-tertiary border border-border-color rounded-lg overflow-hidden w-80 animate-pulse ${inMessage ? 'mt-1' : ''}`}>
        <div className="p-4">
          <div className="h-3 bg-bg-secondary rounded w-40 mb-3" />
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-bg-secondary flex-shrink-0" />
            <div className="space-y-2 flex-1">
              <div className="h-4 bg-bg-secondary rounded w-32" />
              <div className="h-3 bg-bg-secondary rounded w-20" />
            </div>
          </div>
          <div className="mt-4 h-8 bg-bg-secondary rounded-lg" />
        </div>
      </div>
    );
  }

  // ── Error / invalid ───────────────────────────────────────────────────────
  if (loadError || !invitation) {
    return (
      <div className={`bg-bg-tertiary border border-border-color rounded-lg p-3 w-80 ${inMessage ? 'mt-1' : ''}`}>
        <div className="flex items-center gap-2 text-text-muted">
          <AlertTriangle className="h-4 w-4 text-error flex-shrink-0" />
          <div>
            <p className="text-xs font-semibold text-text-secondary">Invitation invalide</p>
            <p className="text-xs text-text-muted">Ce lien est expiré ou a été révoqué.</p>
          </div>
        </div>
      </div>
    );
  }

  const info = invitation.server_info || (invitation as any).server;

  // ── Discord-style invite card ─────────────────────────────────────────────
  return (
    <div className={`bg-bg-tertiary border border-border-color hover:border-border-color/70 rounded-lg overflow-hidden w-80 select-none ${inMessage ? 'mt-1' : ''}`}>
      {/* Header label */}
      <div className="px-4 pt-3 pb-0">
        <p className="text-xs font-bold uppercase tracking-wider text-text-muted">
          Vous avez été invité à rejoindre un serveur
        </p>
      </div>

      {/* Server info row */}
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Server icon */}
        <div className="w-14 h-14 rounded-2xl bg-bg-secondary border border-border-color flex-shrink-0 overflow-hidden flex items-center justify-center">
          {info?.icon_url ? (
            <img
              src={info.icon_url}
              alt={info.name}
              className="w-14 h-14 object-cover"
            />
          ) : (
            <Server className="w-6 h-6 text-accent-color" />
          )}
        </div>

        {/* Name + member count */}
        <div className="flex-1 min-w-0">
          <p className="font-bold text-text-primary text-base truncate">{info?.name}</p>
          <div className="flex items-center gap-3 mt-0.5">
            <span className="flex items-center gap-1 text-xs text-text-muted">
              <span className="w-2 h-2 rounded-full bg-success inline-block" />
              {(info?.member_count ?? 0).toLocaleString()} membres
            </span>
            {invitation.max_uses > 0 && (
              <span className="flex items-center gap-1 text-xs text-text-muted">
                <Shield className="h-3 w-3" />
                {invitation.uses}/{invitation.max_uses} utilisations
              </span>
            )}
          </div>
          {info?.description && (
            <p className="text-xs text-text-muted mt-1 line-clamp-1">{info.description}</p>
          )}
        </div>
      </div>

      {/* Action button */}
      <div className="px-4 pb-4">
        {joinError && <p className="text-xs text-error mb-2">{joinError}</p>}
        {isAlreadyMember || joined ? (
          <div className="flex items-center justify-center gap-2 h-9 rounded-lg bg-success/20 text-success text-sm font-semibold">
            <Check className="h-4 w-4" />
            {joined ? 'Serveur rejoint !' : 'Déjà membre'}
          </div>
        ) : (
          <button
            onClick={handleJoin}
            disabled={joining}
            className="w-full h-9 rounded-lg bg-success hover:bg-success/90 text-white text-sm font-bold transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {joining
              ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : 'Rejoindre le serveur'
            }
          </button>
        )}
      </div>
    </div>
  );
};

export default InviteCard;
